import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";

import type {
  DateOfBirth,
  GuestAccessLinkOpened,
} from "@thalamos/haven-common";

import guestApi from "../../api/guest/api.js";
import { guestRoute } from "../../routing/guestRoute.js";
import {
  GuestSignInContent,
  type GuestSignIn,
  type GuestSignInActions,
} from "./GuestSignInContent.js";

const { guest } = guestApi;

/** Where a link just opened stands, as the page shows it. */
export const signInFor = (opened: GuestAccessLinkOpened): GuestSignIn => {
  const { state, ...link } = opened;

  return match(state)
    .returnType<GuestSignIn>()
    .with("open", () => ({ kind: "welcome", link }))
    .with("declined", () => ({ kind: "declined", link }))
    .with(
      "locked",
      "sent",
      "ended",
      "expired",
      "address_refused",
      (closed) => ({ kind: "closed", state: closed, link }),
    )
    .exhaustive();
};

/**
 * The token in an address's fragment, or empty where there is none or it is
 * not percent-encoded as a link Haven issued would be.
 */
const tokenIn = (hash: string): string => {
  try {
    return decodeURIComponent(hash.replace(/^#/, ""));
  } catch {
    return "";
  }
};

/**
 * Where a guest's emailed link lands: opens the link named by the token in
 * the address's fragment, and walks the guest through signing in, to the
 * assessment they were asked for or the assessments shared with them.
 *
 * The server keeps where signing in has got to. A step it refuses as out of
 * turn, or after the session lapsed, is answered by opening the link again,
 * which says where it now stands.
 */
export const GuestSignInPage = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const token = tokenIn(hash);
  const [signIn, setSignIn] = useState<GuestSignIn>({ kind: "opening" });

  const open = useCallback(async (): Promise<GuestSignIn> => {
    if (token === "") {
      return { kind: "no_link" };
    }

    const opened = await guest.openLink(token).catch(() => undefined);

    if (opened === undefined) {
      return { kind: "unavailable" };
    }

    return opened.data === undefined
      ? { kind: "no_link" }
      : signInFor(opened.data);
  }, [token]);

  const openAgain = useCallback(async () => {
    setSignIn(await open());
  }, [open]);

  useEffect(() => {
    void openAgain();
  }, [openAgain]);

  const link = match(signIn)
    .with(
      { kind: "opening" },
      { kind: "no_link" },
      { kind: "unavailable" },
      () => undefined,
    )
    .otherwise((withLink) => withLink.link);

  const signedIn = () => {
    navigate(
      match(link?.purpose)
        .with("read_shared_assessments", () => guestRoute.shared())
        .with("complete_assessment", undefined, () => guestRoute.assessment())
        .exhaustive(),
      { replace: true },
    );
  };

  const actions: GuestSignInActions = {
    onRetry: openAgain,
    onSendCode: async () => {
      const sent = await guest.sendCode().catch(() => undefined);

      if (sent?.status === 204 && link !== undefined) {
        setSignIn({ kind: "code", link });
        return;
      }

      // Refused while the link still opens: a code went a moment ago, and
      // that code can still be entered.
      const reopened = await open();
      setSignIn(
        sent?.status === 409 && reopened.kind === "welcome"
          ? {
              kind: "code",
              link: reopened.link,
              problem: { kind: "sent_a_moment_ago" },
            }
          : reopened,
      );
    },
    onEnterCode: async (code: string) => {
      const entered = await guest.enterCode(code).catch(() => undefined);

      if (entered?.data === undefined || link === undefined) {
        await openAgain();
        return;
      }

      setSignIn(
        match(entered.data)
          .returnType<GuestSignIn>()
          .with({ outcome: "accepted" }, () => ({
            kind: "date_of_birth",
            link,
          }))
          .with({ outcome: "wrong" }, ({ triesLeft }) => ({
            kind: "code",
            link,
            problem: { kind: "wrong", triesLeft },
          }))
          .with({ outcome: "expired" }, () => ({
            kind: "code",
            link,
            problem: { kind: "expired" },
          }))
          .with({ outcome: "locked" }, () => ({
            kind: "closed",
            state: "locked",
            link,
          }))
          .exhaustive(),
      );
    },
    onEnterDateOfBirth: async (dateOfBirth: DateOfBirth) => {
      const entered = await guest
        .enterDateOfBirth(dateOfBirth)
        .catch(() => undefined);

      if (entered?.data === undefined || link === undefined) {
        await openAgain();
        return;
      }

      const next = match(entered.data)
        .returnType<GuestSignIn | "signed_in" | "open_again">()
        .with({ outcome: "accepted", next: "signed_in" }, () => "signed_in")
        .with({ outcome: "accepted", next: "agreement" }, ({ wording }) => ({
          kind: "agreement",
          link,
          wording,
        }))
        .with({ outcome: "wrong" }, ({ triesLeft }) => ({
          kind: "date_of_birth",
          link,
          triesLeft,
        }))
        .with({ outcome: "locked" }, () => ({
          kind: "closed",
          state: "locked",
          link,
        }))
        // An expired outcome belongs to a code, not a date, so the link is
        // asked where it stands.
        .with({ outcome: "expired" }, () => "open_again")
        .exhaustive();

      if (next === "signed_in") {
        signedIn();
      } else if (next === "open_again") {
        await openAgain();
      } else {
        setSignIn(next);
      }
    },
    onAcceptAgreement: async (wordingDigest: string) => {
      const accepted = await guest
        .acceptAgreement(wordingDigest)
        .catch(() => undefined);

      if (accepted?.status === 204) {
        signedIn();
        return;
      }

      await openAgain();
    },
    onDecline: async () => {
      const declined = await guest.decline().catch(() => undefined);

      if (declined?.status === 204 && link !== undefined) {
        setSignIn({ kind: "declined", link });
        return;
      }

      await openAgain();
    },
  };

  return <GuestSignInContent signIn={signIn} actions={actions} />;
};
